import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, HandCoins, Camera, MapPin, FlagCheckered, RoadHorizon, Signature, Gift, X, Timer } from "@phosphor-icons/react";
import { DetentionDialog } from "@/components/DetentionDialog";
import DocumentsDialog from "@/components/DocumentsDialog";
import { MoneyInput } from "@/components/MoneyInput";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { BR_STATES } from "@/lib/br";
import { useAuth } from "@/lib/auth";
import { parseNum, parseInt10, fmtDate, fmtN } from "@/lib/utils";

const EMPTY = {
  viagem_id: "",  // set when adding to existing viagem
  vehicle_id: "", date_departure: "", date_loading: "", km_start: "",
  origin: "", origin_uf: "", destination: "", destination_uf: "", client: "", client_id: "", client_type: "transportadora",
  tons: "", price_per_ton: "", total_freight: 0,
  discount_insurance: "", discount_carrier_fee: "", discount_taxes: "", discount_others: "",
  net_freight: 0, commission_percent: "", commission_base: "bruto", commission_value: 0,
  nfe: "", cte: "", ciot: "", mdfe_closed: false,
  toll_payer: "contratante", toll_freight_included: false, toll_deduct_freight: false,
  advance_driver: "", truck_expenses: "",
  freight_received_method: "dinheiro", freight_received_by: "empresa", status: "em_andamento",
  bonuses: []
};

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const BONUS_TYPES = [
  { value: "on_time_trip",       label: "⏱️ Cumpriu horário na viagem" },
  { value: "professionalism",    label: "🌟 Bom profissionalismo" },
  { value: "best_avg_trip",      label: "⛽ Melhor média (viagem)" },
  { value: "best_avg_month",     label: "🏆 Melhor média do mês" },
  { value: "best_avg_year",      label: "🥇 Melhor média do ano" },
  { value: "standby_daily",      label: "🕒 Diária por tempo parado" },
  { value: "other",              label: "➕ Outro" },
];
const BONUS_TYPE_LABEL = BONUS_TYPES.reduce((a, b) => ({ ...a, [b.value]: b.label }), {});

export default function Trips() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [viagens, setViagens] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [viagemChoice, setViagemChoice] = useState("nova"); // "nova" or "existente"
  const [confirmPwd, setConfirmPwd] = useState("");
  const [docsTrip, setDocsTrip] = useState(null);
  const [detTripId, setDetTripId] = useState(null);
  const [docsInitialLabel, setDocsInitialLabel] = useState("canhoto");
  const [docsAutoCamera, setDocsAutoCamera] = useState(false);
  const [docsCountByTrip, setDocsCountByTrip] = useState({}); // { trip_id: { total, canhoto } }
  const [closeViagem, setCloseViagem] = useState(null); // viagem being closed
  const [closeKm, setCloseKm] = useState("");
  const [closeDate, setCloseDate] = useState("");
  const [showQuickClient, setShowQuickClient] = useState(false);
  const [quickClientForm, setQuickClientForm] = useState({ name: "", phone: "", client_type: "transportadora" });

  const openViagens = useMemo(() => viagens.filter((v) => v.status === "aberta"), [viagens]);
  const viagensById = useMemo(() => Object.fromEntries(viagens.map((v) => [v.id, v])), [viagens]);

  const load = async () => {
    const [t, v, vh, cl, docs] = await Promise.all([
      api.get("/trips"),
      api.get("/viagens"),
      api.get("/vehicles"),
      api.get("/clients"),
      api.get("/documents").catch(() => ({ data: [] })),
    ]);
    setItems(t.data); setViagens(v.data); setVehicles(vh.data); setClients(cl.data);
    const counts = {};
    for (const d of docs.data || []) {
      if (!d.trip_id) continue;
      counts[d.trip_id] = counts[d.trip_id] || { total: 0, canhoto: 0 };
      counts[d.trip_id].total += 1;
      if (d.label === "canhoto") counts[d.trip_id].canhoto += 1;
    }
    setDocsCountByTrip(counts);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const h = (e) => { if (["trip", "document", "viagem", "client"].includes(e.detail?.entity)) load(); };
    window.addEventListener("live-event", h);
    return () => window.removeEventListener("live-event", h);
  }, []);

  // Track which field was last edited manually so we know the calc direction
  const [priceEditDir, setPriceEditDir] = useState("price"); // "price" -> compute total; "total" -> compute price

  // Handlers for tonnage/price/total to keep them in sync bidirectionally
  const onTonsChange = (e) => {
    const raw = e.target.value;
    setForm((f) => {
      const t = parseNum(raw);
      // If user last edited "total", keep the total and recompute price
      if (priceEditDir === "total") {
        const totalNum = parseNum(f.total_freight);
        const newPrice = t > 0 ? +(totalNum / t).toFixed(2) : 0;
        return { ...f, tons: raw, price_per_ton: newPrice };
      }
      // Otherwise keep price and recompute total
      const p = parseNum(f.price_per_ton);
      return { ...f, tons: raw, total_freight: +(t * p).toFixed(2) };
    });
  };
  const onPriceChange = (v) => {
    setPriceEditDir("price");
    setForm((f) => {
      const t = parseNum(f.tons);
      return { ...f, price_per_ton: v, total_freight: +(t * (v || 0)).toFixed(2) };
    });
  };
  const onTotalChange = (v) => {
    setPriceEditDir("total");
    setForm((f) => {
      const t = parseNum(f.tons);
      const newPrice = t > 0 ? +((v || 0) / t).toFixed(2) : 0;
      return { ...f, total_freight: v, price_per_ton: newPrice };
    });
  };

  // ---------- Bonificações & Diárias ----------
  const addBonus = () => setForm((f) => ({
    ...f,
    bonuses: [
      ...(f.bonuses || []),
      { id: `b_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, type: "on_time_trip", description: "", value: 0, paid_by: "own_company", date: "" },
    ],
  }));
  const updateBonus = (id, patch) => setForm((f) => ({
    ...f,
    bonuses: (f.bonuses || []).map((b) => (b.id === id ? { ...b, ...patch } : b)),
  }));
  const removeBonus = (id) => setForm((f) => ({
    ...f,
    bonuses: (f.bonuses || []).filter((b) => b.id !== id),
  }));
  const bonusesTotal = useMemo(
    () => (form.bonuses || []).reduce((s, b) => s + (parseNum(b.value) || 0), 0),
    [form.bonuses]
  );

  // Auto-calc net freight & commission (depends on total_freight, discounts, and commission %)
  useEffect(() => {
    const total = parseNum(form.total_freight);
    const net = total - parseNum(form.discount_insurance) - parseNum(form.discount_carrier_fee) - parseNum(form.discount_taxes) - parseNum(form.discount_others);
    const base = form.commission_base === "liquido" ? net : total;
    const commission = base * (parseNum(form.commission_percent) / 100);
    setForm((f) => ({ ...f, net_freight: net, commission_value: commission }));
    // eslint-disable-next-line
  }, [form.total_freight, form.discount_insurance, form.discount_carrier_fee, form.discount_taxes, form.discount_others, form.commission_percent, form.commission_base]);

  const set = (k, isNum = false) => (e) => {
    const raw = e?.target ? e.target.value : e;
    // For numeric fields, keep the raw string (allow "38," or "38.5" or "38,5" while typing).
    // Conversion happens in the auto-calc useEffect (parseNum handles both) and on submit.
    setForm((f) => ({ ...f, [k]: raw }));
  };

  const notifyArrival = async (t) => {
    if (!window.confirm(`Confirmar chegada em ${t.destination}?`)) return;
    try {
      await api.post(`/trips/${t.id}/arrival`);
      toast.success("Empresa notificada!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const TRIP_NUM_FIELDS = ["tons", "price_per_ton", "discount_insurance", "discount_carrier_fee", "discount_taxes", "discount_others", "commission_percent", "advance_driver", "truck_expenses"];
  const TRIP_KM_FIELDS = ["km_start", "km_end"];

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Build payload
      const payload = { ...form };
      // Convert all numeric fields (accept vírgula and ponto)
      TRIP_NUM_FIELDS.forEach((k) => { payload[k] = parseNum(payload[k]); });
      TRIP_KM_FIELDS.forEach((k) => { payload[k] = parseInt10(payload[k]); });
      payload.total_freight = parseNum(payload.total_freight);
      payload.net_freight = parseNum(payload.net_freight);
      payload.commission_value = parseNum(payload.commission_value);
      if (viagemChoice === "existente") {
        if (!payload.viagem_id) { toast.error("Selecione uma viagem em aberto"); return; }
        // KM comes from viagem — don't send km_start on the frete
        delete payload.km_start;
      } else {
        // Nova viagem — viagem_id vazio faz o backend criar automaticamente
        payload.viagem_id = "";
      }
      if (editing) {
        await api.post("/auth/confirm-password", { password: confirmPwd });
        await api.put(`/trips/${editing.id}`, payload);
        toast.success("Frete atualizado");
      } else {
        await api.post("/trips", payload);
        toast.success(viagemChoice === "existente" ? "Frete adicionado à viagem" : "Nova viagem + frete criados");
      }
      setOpen(false); setEditing(null); setForm(EMPTY); setConfirmPwd(""); setViagemChoice("nova"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir frete? Isso removerá abastecimentos e despesas relacionadas.")) return;
    await api.delete(`/trips/${id}`); toast.success("Removido"); load();
  };

  const removeViagem = async (v) => {
    if (!window.confirm(`Excluir Viagem ${v.viagem_number} INTEIRA e TODOS seus fretes/abastecimentos/despesas?`)) return;
    try { await api.delete(`/viagens/${v.id}`); toast.success("Viagem removida"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const startCloseViagem = (v) => {
    setCloseViagem(v);
    setCloseKm("");
    setCloseDate(new Date().toISOString().slice(0, 10));
  };

  const finalizeViagem = async (e) => {
    e.preventDefault();
    const km = parseInt10(closeKm);
    if (!km || km <= (closeViagem?.km_start || 0)) {
      toast.error("KM final deve ser maior que o KM inicial (" + (closeViagem?.km_start || 0) + ")");
      return;
    }
    try {
      await api.post(`/viagens/${closeViagem.id}/close`, { km_end: km, date_end: closeDate });
      toast.success(`Viagem ${closeViagem.viagem_number} finalizada. ${km - (closeViagem?.km_start || 0)} km rodados.`);
      setCloseViagem(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const saveQuickClient = async (e) => {
    e.preventDefault();
    if (!quickClientForm.name?.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      const { data } = await api.post("/clients", quickClientForm);
      toast.success(`Cliente "${data.name}" cadastrado`);
      // Reload list, then select new client
      const cl = await api.get("/clients");
      setClients(cl.data);
      setForm((f) => ({ ...f, client_id: data.id, client: data.name, client_type: data.client_type || "transportadora" }));
      setShowQuickClient(false);
      setQuickClientForm({ name: "", phone: "", client_type: "transportadora" });
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar cliente"); }
  };

  const startEdit = (t) => {
    setEditing(t);
    setForm({ ...EMPTY, ...t });
    setViagemChoice(t.viagem_id ? "existente" : "nova");
    setOpen(true);
  };
  const startNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, contractor_name: user?.contractor_name || "", contractor_cnpj: user?.contractor_cnpj || "" });
    setViagemChoice(openViagens.length > 0 ? "existente" : "nova");
    setForm((f) => ({ ...f, viagem_id: openViagens[0]?.id || "" }));
    setOpen(true);
  };

  // Sort fretes: by viagem_number desc, then frete_number asc
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const va = viagensById[a.viagem_id]?.viagem_number || 0;
      const vb = viagensById[b.viagem_id]?.viagem_number || 0;
      if (va !== vb) return vb - va;
      return (a.frete_number || 0) - (b.frete_number || 0);
    });
  }, [items, viagensById]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
            <HandCoins size={32} weight="duotone" className="text-blue-600" />
            <span>Viagens e Fretes</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Cada viagem pode ter vários fretes. KM é lançado uma vez só (início e fim).</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={startNew} data-testid="btn-new-trip" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-1.5"><Plus size={16} /> Novo Frete</Button></DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Editar" : "Novo"} Frete</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 xl:grid-cols-4 gap-3">
              {/* Viagem selector */}
              {!editing && (
                <div className="col-span-2 md:col-span-4 border-2 border-blue-200 rounded-lg p-3 bg-blue-50/50 space-y-2" data-testid="viagem-selector">
                  <Label className="text-blue-800 font-bold uppercase tracking-wider text-xs">Este frete pertence a qual viagem?</Label>
                  <RadioGroup value={viagemChoice} onValueChange={setViagemChoice} className="flex flex-col sm:flex-row gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value="nova" id="v-nova" data-testid="opt-nova-viagem" />
                      <span className="text-sm">🆕 <strong>Nova viagem</strong> (Viagem {(viagens[0]?.viagem_number || 0) + 1})</span>
                    </label>
                    <label className={`flex items-center gap-2 cursor-pointer ${openViagens.length === 0 ? "opacity-40" : ""}`}>
                      <RadioGroupItem value="existente" id="v-existente" data-testid="opt-existente-viagem" disabled={openViagens.length === 0} />
                      <span className="text-sm">➕ <strong>Adicionar à viagem em andamento</strong></span>
                    </label>
                  </RadioGroup>
                  {viagemChoice === "existente" && (
                    <Select value={form.viagem_id} onValueChange={set("viagem_id")}>
                      <SelectTrigger data-testid="select-open-viagem"><SelectValue placeholder="Selecione a viagem em aberto" /></SelectTrigger>
                      <SelectContent>
                        {openViagens.map((v) => (
                          <SelectItem key={v.id} value={v.id}>Viagem {v.viagem_number} — {fmtDate(v.date_start)} — KM saída: {fmtN(v.km_start, 0)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {viagemChoice === "nova" && (
                    <p className="text-xs text-blue-700">Ao salvar, vamos criar uma nova viagem numerada e este será o Frete #1 dela.</p>
                  )}
                </div>
              )}

              <div className="col-span-2 md:col-span-4"><Label>Veículo</Label>
                <Select value={form.vehicle_id} onValueChange={set("vehicle_id")}>
                  <SelectTrigger data-testid="trip-vehicle"><SelectValue placeholder="Selecione um veículo" /></SelectTrigger>
                  <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate_truck} — {v.model}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data saída</Label><Input type="date" value={form.date_departure} onChange={set("date_departure")} /></div>
              <div><Label>Data carregamento</Label><Input type="date" value={form.date_loading} onChange={set("date_loading")} /></div>
              {viagemChoice === "nova" && !editing && (
                <div className="col-span-2"><Label>KM Saída (início da viagem)</Label><Input type="text" inputMode="decimal" value={form.km_start} onChange={set("km_start", true)} placeholder="ex: 125000" data-testid="trip-km-start" /></div>
              )}
              {viagemChoice === "existente" && form.viagem_id && (
                <div className="col-span-2 md:col-span-4 text-sm bg-emerald-50 border border-emerald-200 rounded-md p-2">
                  ✅ KM saída herdado da viagem: <strong>{fmtN(viagensById[form.viagem_id]?.km_start, 0)}</strong>. Você lança o KM final apenas quando encerrar a viagem.
                </div>
              )}
              <div className="col-span-2">
                <Label>Origem</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <CityAutocomplete value={form.origin} uf={form.origin_uf} onChange={set("origin")} placeholder="Digite a cidade" data-testid="trip-origin" />
                  </div>
                  <Select value={form.origin_uf} onValueChange={(v) => { set("origin_uf")(v); }}>
                    <SelectTrigger className="w-24" data-testid="trip-origin-uf"><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent>{BR_STATES.map(([uf]) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="col-span-2">
                <Label>Destino</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <CityAutocomplete value={form.destination} uf={form.destination_uf} onChange={set("destination")} placeholder="Digite a cidade" data-testid="trip-destination" />
                  </div>
                  <Select value={form.destination_uf} onValueChange={(v) => { set("destination_uf")(v); }}>
                    <SelectTrigger className="w-24" data-testid="trip-destination-uf"><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent>{BR_STATES.map(([uf]) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="col-span-2">
                <Label>Cliente / Transportadora</Label>
                <Select value={form.client_id || "__none"} onValueChange={(v) => {
                  if (v === "__new") { setShowQuickClient(true); return; }
                  if (v === "__none") { setForm((f) => ({ ...f, client_id: "", client: "" })); return; }
                  const c = clients.find((x) => x.id === v);
                  if (c) setForm((f) => ({ ...f, client_id: c.id, client: c.name, client_type: c.client_type || "transportadora" }));
                }}>
                  <SelectTrigger data-testid="trip-client-select"><SelectValue placeholder="Selecione um cliente" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— Sem cliente cadastrado (digitar livre) —</SelectItem>
                    <SelectItem value="__new" data-testid="trip-client-new"><span className="text-blue-700 font-bold">➕ Cadastrar novo cliente</span></SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}{c.city ? ` — ${c.city}/${c.uf || ""}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!form.client_id && (
                  <Input className="mt-2" value={form.client} onChange={set("client")} placeholder="Ou digite o nome do cliente" data-testid="trip-client-freetext" />
                )}
              </div>
              {user?.role === "autonomo" && (
                <div className="col-span-2 rounded-md border border-blue-200 bg-blue-50/50 p-3 space-y-2" data-testid="trip-contractor-box">
                  <Label>Contratante (para quem você presta o serviço)</Label>
                  <Select value={form.contractor_client_id || (form.contractor_name ? "__profile" : "__none")} onValueChange={(v) => {
                    if (v === "__none") return setForm((f) => ({ ...f, contractor_client_id: null, contractor_name: "", contractor_cnpj: "" }));
                    if (v === "__profile") return setForm((f) => ({ ...f, contractor_client_id: null, contractor_name: user?.contractor_name || "", contractor_cnpj: user?.contractor_cnpj || "" }));
                    const c = clients.find((x) => x.id === v);
                    if (c) setForm((f) => ({ ...f, contractor_client_id: c.id, contractor_name: c.name, contractor_cnpj: c.doc_number || "" }));
                  }}>
                    <SelectTrigger data-testid="trip-contractor-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— Usar a do Perfil / nenhuma —</SelectItem>
                      {user?.contractor_name && <SelectItem value="__profile">{user.contractor_name} (do Perfil)</SelectItem>}
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.doc_number ? ` — ${c.doc_number}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {form.contractor_name && <p className="text-xs text-slate-600" data-testid="trip-contractor-selected">{form.contractor_name}{form.contractor_cnpj ? ` · CNPJ ${form.contractor_cnpj}` : ""}</p>}
                </div>
              )}
              <div className="col-span-2"><Label>Tipo</Label>
                <Select value={form.client_type} onValueChange={set("client_type")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="transportadora">Transportadora</SelectItem><SelectItem value="particular">Cliente Particular</SelectItem><SelectItem value="fornecedor">Fornecedor</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Toneladas</Label><Input type="text" inputMode="decimal" placeholder="ex: 38,5" value={form.tons} onChange={onTonsChange} data-testid="trip-tons" /></div>
              <div><Label>R$/Ton</Label><MoneyInput value={form.price_per_ton} onChange={onPriceChange} placeholder="0,00" data-testid="trip-price" /></div>
              <div className="col-span-2">
                <Label className="flex items-center gap-1">Total Frete <span className="text-[10px] font-normal text-blue-600">(auto ou digite direto)</span></Label>
                <MoneyInput value={form.total_freight} onChange={onTotalChange} placeholder="0,00" className="bg-blue-50 font-bold" data-testid="trip-total-freight" />
              </div>

              <div className="col-span-4 mt-2 text-xs font-bold uppercase tracking-wider text-blue-700">Descontos</div>
              <div><Label>Seguro</Label><MoneyInput value={form.discount_insurance} onChange={(v) => setForm((f) => ({ ...f, discount_insurance: v }))} placeholder="0,00" /></div>
              <div><Label>Taxa Transp.</Label><MoneyInput value={form.discount_carrier_fee} onChange={(v) => setForm((f) => ({ ...f, discount_carrier_fee: v }))} placeholder="0,00" /></div>
              <div><Label>Impostos</Label><MoneyInput value={form.discount_taxes} onChange={(v) => setForm((f) => ({ ...f, discount_taxes: v }))} placeholder="0,00" /></div>
              <div><Label>Outros</Label><MoneyInput value={form.discount_others} onChange={(v) => setForm((f) => ({ ...f, discount_others: v }))} placeholder="0,00" /></div>
              <div className="col-span-4"><Label>Frete Líquido (auto)</Label><Input readOnly value={fmt(form.net_freight)} className="bg-emerald-50 font-bold" /></div>

              <div className="col-span-4 mt-2 text-xs font-bold uppercase tracking-wider text-blue-700">Comissão</div>
              <div><Label>% Comissão</Label><Input type="text" inputMode="decimal" value={form.commission_percent} onChange={set("commission_percent", true)} /></div>
              <div><Label>Base</Label>
                <Select value={form.commission_base} onValueChange={set("commission_base")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="bruto">Bruto</SelectItem><SelectItem value="liquido">Líquido</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Comissão (auto)</Label><Input readOnly value={fmt(form.commission_value)} className="bg-yellow-50 font-bold" /></div>

              <div className="col-span-4 mt-2 flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
                  <Gift size={14} weight="fill" /> Bonificações & Diárias (Motorista)
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addBonus} data-testid="btn-add-bonus" className="h-7 gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800">
                  <Plus size={12} /> Adicionar
                </Button>
              </div>
              {(form.bonuses || []).length === 0 ? (
                <div className="col-span-4 text-xs text-slate-500 bg-slate-50 border border-dashed rounded p-2">
                  Nenhuma bonificação lançada. Clique em <strong>+ Adicionar</strong> pra registrar bônus por cumprimento de horário, profissionalismo, melhor média, ou diárias por tempo parado.
                </div>
              ) : (
                <div className="col-span-4 space-y-2" data-testid="bonuses-list">
                  {form.bonuses.map((b) => (
                    <div key={b.id} className="grid grid-cols-12 gap-2 items-end border border-emerald-100 rounded-md p-2 bg-emerald-50/40">
                      <div className="col-span-12 md:col-span-4">
                        <Label className="text-[10px] uppercase tracking-wider text-emerald-800">Tipo</Label>
                        <Select value={b.type} onValueChange={(v) => updateBonus(b.id, { type: v })}>
                          <SelectTrigger data-testid={`bonus-type-${b.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {BONUS_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-6 md:col-span-3">
                        <Label className="text-[10px] uppercase tracking-wider text-emerald-800">Valor</Label>
                        <MoneyInput value={b.value} onChange={(v) => updateBonus(b.id, { value: v })} placeholder="0,00" data-testid={`bonus-value-${b.id}`} />
                      </div>
                      <div className="col-span-6 md:col-span-3">
                        <Label className="text-[10px] uppercase tracking-wider text-emerald-800">Pago por</Label>
                        <Select value={b.paid_by} onValueChange={(v) => updateBonus(b.id, { paid_by: v })}>
                          <SelectTrigger data-testid={`bonus-paidby-${b.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="own_company">🏢 Empresa dele (empregador)</SelectItem>
                            <SelectItem value="client_company">🚚 Empresa contratante</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-10 md:col-span-11">
                        <Label className="text-[10px] uppercase tracking-wider text-emerald-800">Observação (opcional)</Label>
                        <Input value={b.description || ""} onChange={(e) => updateBonus(b.id, { description: e.target.value })} placeholder="Ex: chegou 2 dias antes do prazo" data-testid={`bonus-desc-${b.id}`} />
                      </div>
                      <div className="col-span-2 md:col-span-1 flex justify-end">
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeBonus(b.id)} data-testid={`btn-remove-bonus-${b.id}`} title="Remover">
                          <X size={16} className="text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="text-right text-sm">
                    <span className="text-slate-600">Total bonificações:</span>{" "}
                    <strong className="text-emerald-800" data-testid="bonuses-total">{fmt(bonusesTotal)}</strong>
                    <span className="text-xs text-slate-500 ml-1">(será somado ao saldo do motorista)</span>
                  </div>
                </div>
              )}

              <div className="col-span-4 mt-2 text-xs font-bold uppercase tracking-wider text-blue-700">Documentos</div>
              <div><Label>NFe</Label><Input value={form.nfe} onChange={set("nfe")} /></div>
              <div><Label>CTe</Label><Input value={form.cte} onChange={set("cte")} /></div>
              <div><Label>CIOT</Label><Input value={form.ciot} onChange={set("ciot")} /></div>
              <div className="flex items-center gap-2 mt-6"><Checkbox id="mdf" checked={!!form.mdfe_closed} onCheckedChange={set("mdfe_closed")} /><Label htmlFor="mdf">MDFe encerrado</Label></div>

              <div className="col-span-4 mt-2 text-xs font-bold uppercase tracking-wider text-blue-700">Pedágio</div>
              <div className="col-span-2"><Label>Pago por</Label>
                <Select value={form.toll_payer} onValueChange={set("toll_payer")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="contratante">Contratante</SelectItem><SelectItem value="motorista">Motorista</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-6"><Checkbox id="tfi" checked={!!form.toll_freight_included} onCheckedChange={set("toll_freight_included")} /><Label htmlFor="tfi">Frete cheio (inclui pedágio)</Label></div>
              <div className="flex items-center gap-2 mt-6"><Checkbox id="tdf" checked={!!form.toll_deduct_freight} onCheckedChange={set("toll_deduct_freight")} /><Label htmlFor="tdf">Descontar do frete</Label></div>

              <div className="col-span-4 mt-2 text-xs font-bold uppercase tracking-wider text-blue-700">Adiantamentos & Recebimentos</div>
              <div className="col-span-2"><Label>Adiantamento Motorista</Label><MoneyInput value={form.advance_driver} onChange={(v) => setForm((f) => ({ ...f, advance_driver: v }))} placeholder="0,00" /></div>
              <div className="col-span-2"><Label>Despesas do caminhão</Label><MoneyInput value={form.truck_expenses} onChange={(v) => setForm((f) => ({ ...f, truck_expenses: v }))} placeholder="0,00" /></div>
              <div className="col-span-2"><Label>Forma recebimento</Label>
                <Select value={form.freight_received_method} onValueChange={set("freight_received_method")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="carta_frete">Carta Frete</SelectItem>
                    <SelectItem value="deposito">Depósito</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Recebido por</Label>
                <Select value={form.freight_received_by} onValueChange={set("freight_received_by")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="empresa">Empresa</SelectItem><SelectItem value="motorista">Motorista</SelectItem></SelectContent>
                </Select>
              </div>

              {editing && (
                <div className="col-span-4 border-t pt-3">
                  <Label>Confirme sua senha para salvar alterações</Label>
                  <Input type="password" data-testid="confirm-password-trip" required value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} />
                </div>
              )}
              <DialogFooter className="col-span-4"><Button type="submit" data-testid="btn-save-trip" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white">{editing ? "Salvar" : "Cadastrar Frete"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Open Viagens summary card */}
      {openViagens.length > 0 && (
        <Card className="border-blue-300 bg-blue-50/40">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><RoadHorizon size={20} weight="duotone" className="text-blue-700" /> Viagens em andamento ({openViagens.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {openViagens.map((v) => {
              const fretesOfV = items.filter((i) => i.viagem_id === v.id);
              return (
                <div key={v.id} data-testid={`viagem-open-${v.viagem_number}`} className="flex items-center justify-between flex-wrap gap-2 bg-white border rounded-md p-3">
                  <div>
                    <div className="font-bold text-blue-900">Viagem {v.viagem_number}</div>
                    <div className="text-xs text-slate-600">Início: {fmtDate(v.date_start)} · KM saída: <strong>{fmtN(v.km_start, 0)}</strong> · Fretes: <strong>{fretesOfV.length}</strong></div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => startCloseViagem(v)} data-testid={`btn-close-viagem-${v.viagem_number}`} className="bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white gap-1"><FlagCheckered size={14} weight="fill" /> Finalizar Viagem</Button>
                    <Button size="sm" variant="ghost" onClick={() => removeViagem(v)} className="text-red-600"><Trash size={14} /></Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-lg">Fretes ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? <p className="text-muted-foreground text-sm">Nenhum frete cadastrado ainda.</p> : (
            <Table>
              <TableHeader><TableRow><TableHead>V/F</TableHead><TableHead>Data</TableHead><TableHead>Rota</TableHead><TableHead>Cliente</TableHead><TableHead className="text-right">Ton</TableHead><TableHead className="text-right">Bruto</TableHead><TableHead className="text-right">Líquido</TableHead><TableHead className="text-right">Comissão</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {sortedItems.map((t) => {
                  const v = viagensById[t.viagem_id];
                  return (
                    <TableRow key={t.id} data-testid={`trip-row-${t.id}`}>
                      <TableCell className="text-xs font-mono font-bold">
                        <span className={`px-2 py-0.5 rounded ${v?.status === "finalizada" ? "bg-slate-100 text-slate-700" : "bg-blue-100 text-blue-800"}`} data-testid={`viagem-frete-tag-${t.id}`}>
                          V{v?.viagem_number || "?"}-F{t.frete_number || "?"}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">{fmtDate(t.date_departure)}</TableCell>
                      <TableCell className="text-xs">{t.origin}{t.origin_uf ? `/${t.origin_uf}` : ""} → {t.destination}{t.destination_uf ? `/${t.destination_uf}` : ""}</TableCell>
                      <TableCell>{t.client}</TableCell>
                      <TableCell className="text-right">{fmtN(t.tons, 3)}</TableCell>
                      <TableCell className="text-right font-semibold">{fmt(t.total_freight)}</TableCell>
                      <TableCell className="text-right text-emerald-700 font-semibold">{fmt(t.net_freight)}</TableCell>
                      <TableCell className="text-right text-yellow-700">{fmt(t.commission_value)}</TableCell>
                      <TableCell className="text-right">
                        {user?.role === "driver" && t.status !== "concluida" && (
                          <Button size="icon" variant="ghost" onClick={() => notifyArrival(t)} data-testid={`btn-arrived-${t.id}`} title="Cheguei ao destino"><MapPin size={16} className="text-emerald-600" weight="fill" /></Button>
                        )}
                        {(user?.role !== "driver" || t.detention_enabled) && (
                          <Button size="icon" variant="ghost" onClick={() => setDetTripId(t.id)} data-testid={`btn-detention-${t.id}`} title="Estadia (tempo parado no cliente)" className="relative">
                            <Timer size={16} weight={t.detention_enabled ? "fill" : "duotone"} className={t.detention_enabled ? "text-amber-600" : "text-slate-500"} />
                          </Button>
                        )}
                        {/* Canhoto/CTe shortcut — camera opens direto */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setDocsInitialLabel("canhoto"); setDocsAutoCamera(true); setDocsTrip(t); }}
                          data-testid={`btn-canhoto-${t.id}`}
                          title="Bater foto do canhoto/CTe"
                          className={`relative gap-1 px-2 ${docsCountByTrip[t.id]?.canhoto ? "text-emerald-700 hover:bg-emerald-50" : "text-blue-700 hover:bg-blue-50"}`}
                        >
                          <Signature size={16} weight={docsCountByTrip[t.id]?.canhoto ? "fill" : "duotone"} />
                          <span className="hidden md:inline text-xs font-semibold">Canhoto</span>
                          {docsCountByTrip[t.id]?.canhoto > 0 && (
                            <span data-testid={`badge-canhoto-${t.id}`} className="ml-0.5 text-[10px] bg-emerald-600 text-white rounded-full min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center">
                              {docsCountByTrip[t.id].canhoto}
                            </span>
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => { setDocsInitialLabel("carregamento"); setDocsAutoCamera(false); setDocsTrip(t); }}
                          data-testid={`btn-docs-${t.id}`}
                          title="Todos os documentos do frete"
                          className="relative"
                        >
                          <Camera size={16} className="text-blue-600" />
                          {docsCountByTrip[t.id]?.total > 0 && (
                            <span className="absolute -top-1 -right-1 text-[9px] bg-blue-600 text-white rounded-full min-w-[14px] h-[14px] px-0.5 inline-flex items-center justify-center">
                              {docsCountByTrip[t.id].total}
                            </span>
                          )}
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => startEdit(t)} data-testid={`btn-edit-trip-${t.id}`}><PencilSimple size={16} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(t.id)}><Trash size={16} className="text-red-600" /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <DetentionDialog trip={items.find((x) => x.id === detTripId) || null} onClose={() => setDetTripId(null)} onTripChanged={load} />

      {/* Finalize Viagem dialog */}
      <Dialog open={!!closeViagem} onOpenChange={(o) => !o && setCloseViagem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FlagCheckered size={22} weight="fill" className="text-emerald-600" /> Finalizar Viagem {closeViagem?.viagem_number}</DialogTitle></DialogHeader>
          <form onSubmit={finalizeViagem} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
              <div>Início: <strong>{fmtDate(closeViagem?.date_start)}</strong></div>
              <div>KM saída: <strong>{closeViagem?.km_start}</strong></div>
              <div>Fretes lançados: <strong>{items.filter(i => i.viagem_id === closeViagem?.id).length}</strong></div>
            </div>
            <div><Label>KM de chegada (obrigatório)</Label>
              <Input type="text" inputMode="decimal" required value={closeKm} onChange={(e) => setCloseKm(e.target.value)} placeholder="ex: 126500" data-testid="close-viagem-km" autoFocus />
              {closeKm && closeViagem?.km_start && parseInt10(closeKm) > closeViagem.km_start && (
                <p className="text-xs text-emerald-700 mt-1">✅ Você vai rodar <strong>{(parseInt10(closeKm) - closeViagem.km_start).toLocaleString("pt-BR")}</strong> km nesta viagem.</p>
              )}
            </div>
            <div><Label>Data de encerramento</Label>
              <Input type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCloseViagem(null)}>Cancelar</Button>
              <Button type="submit" data-testid="btn-confirm-close-viagem" className="bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white gap-1"><FlagCheckered size={16} weight="fill" /> Confirmar Finalização</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DocumentsDialog
        trip={docsTrip}
        initialLabel={docsInitialLabel}
        autoOpenCamera={docsAutoCamera}
        onClose={() => { setDocsTrip(null); setDocsAutoCamera(false); load(); }}
      />

      {/* Quick add client dialog */}
      <Dialog open={showQuickClient} onOpenChange={setShowQuickClient}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>➕ Cadastro rápido de cliente</DialogTitle></DialogHeader>
          <form onSubmit={saveQuickClient} className="space-y-3">
            <div><Label>Nome / Razão Social *</Label>
              <Input required autoFocus value={quickClientForm.name} onChange={(e) => setQuickClientForm({ ...quickClientForm, name: e.target.value })} placeholder="Ex: Transportes ABC Ltda" data-testid="quick-client-name" /></div>
            <div><Label>Telefone (opcional)</Label>
              <Input value={quickClientForm.phone} onChange={(e) => setQuickClientForm({ ...quickClientForm, phone: e.target.value })} placeholder="(65) 9 9999-9999" data-testid="quick-client-phone" /></div>
            <div><Label>Tipo</Label>
              <Select value={quickClientForm.client_type} onValueChange={(v) => setQuickClientForm({ ...quickClientForm, client_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transportadora">Transportadora</SelectItem><SelectItem value="particular">Cliente Particular</SelectItem><SelectItem value="fornecedor">Fornecedor</SelectItem></SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">Você pode completar os dados depois na aba <strong>Clientes</strong>.</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowQuickClient(false)}>Cancelar</Button>
              <Button type="submit" data-testid="btn-save-quick-client" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white">Salvar Cliente</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
